/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @file Hlavní skript platformy pro standardní webové technologie.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

// One-line definice závislostí pro snazší merge.
/* eslint-disable max-len */

// Polyfill se načítají jako první modul aplikace
import '../../polyfill';

import Bottle from 'bottlejs';

import ICAL from 'ical.js';
import UaParser from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';

import AppManager from '../../adapter/web/app/app-manager';
import FileArchiveManager from '../../adapter/web/file/archive/file-archive-manager';
import FileSystemManager from '../../adapter/web/file/system/file-system-manager';
import FileSystemMapper from '../../adapter/web/file/system/file-system-mapper';
import FileTransferManager from '../../adapter/web/file/transfer/file-transfer-manager';
import FirmwareManager from '../../adapter/web/firmware/firmware-manager';
import NetworkManager from '../../adapter/web/network/network-manager';
import PowerManager from '../../adapter/web/power/power-manager';
import ScreenManager from '../../adapter/web/screen/screen-manager';
import ViewerRegistry from '../../adapter/web/viewer/viewer-registry';
import VirtualKeyboardManager from '../../adapter/web/virtual-keyboard/virtual-keyboard-manager';
import AssetDownloader from '../asset/downloader/asset-downloader';
import AutorebootManager from '../autoreboot/autoreboot-manager';
import AutorebootReplacer from '../autoreboot/autoreboot-replacer';
import BasePathResolver from '../basepath/basepath';
import BrowserCapabilities from '../capabilities/browser-capabilities';
import Communicator from '../communicator/communicator';
import ContentSettingsStore from '../content/content-settings-store';
import ContentControllerFactory from '../content/controller/content-controller-factory';
import ContentDownloader from '../content/downloader/content-downloader';
import ContentTaskPlanCronFactory from '../content/task/plan/cron/content-task-plan-cron-factory';
import ContentTaskPlanIteratorFactory from '../content/task/plan/iterator/factory/content-task-plan-iterator-factory';
import DivViewerSourceCreator from '../div/div-viewer-source-creator';
import DivViewerSourceFactory from '../div/div-viewer-source-factory';
import Downloader from '../downloader/downloader';
import FileSystemUnitFinder from '../file-system-unit-finder/file-system-unit-finder';
import FocusNavigator from '../focus/focus-navigator';
import FocusNoop from '../focus/focus-noop';
import HtmlElementRepairer from '../html/html-element-repairer';
import HtmlMediaUtil from '../html/html-media-util';
import HtmlVideoRepairer from '../html/html-video-repairer';
import ImageFileViewerType from '../image/image-file-viewer-type';
import ImageViewerControllerFactory from '../image/image-viewer-controller-factory';
import KioskViewerType from '../kiosk/kiosk-viewer-type';
import LoggerManager from '../logger/logger-manager';
import SimpleLogicalUnitMapper from '../logical-unit-mapper/simple-logical-unit-mapper';
import LongTimers from '../long-timers/long-timers';
import MainCommon from '../main-common';
import PdfFileViewerType from '../pdf/pdf-file-viewer-type';
import PdfViewerControllerFactory from '../pdf/pdf-viewer-controller-factory';
import PdfViewerSourceCreator from '../pdf/pdf-viewer-source-creator';
import PoolAsyncFactory from '../pool/pool-async-factory';
import PoolFactory from '../pool/pool-factory';
import PositionFrameFactory from '../position/position-frame-factory';
import ReplacerControl from '../replacer/control/replacer-control';
import ReplacerPower from '../replacer/power/replacer-power';
import ReplacerReboot from '../replacer/reboot/replacer-reboot';
import Replacer from '../replacer/replacer';
import ReplacerScreen from '../replacer/screen/replacer-screen';
import ReplacerSound from '../replacer/sound/replacer-sound';
import ReportManager from '../report/report-manager';
import StatusInfoDialogOpenStrategySequence from '../status-info-dialog/open-strategy/status-info-dialog-open-strategy-sequence';
import StatusInfoDialogOpenStrategyShortcut from '../status-info-dialog/open-strategy/status-info-dialog-open-strategy-shortcut';
import StatusInfoDialogController from '../status-info-dialog/status-info-dialog-controller';
import StreamViewerType from '../stream/stream-viewer-type';
import UnixTimestamp from '../unix-timestamp/unix-timestamp';
import Updater from '../updater/updater';
import ViewerControllerVideoFactory from '../viewer/controller/viewer-controller-video-factory';
import ViewerDelayerFactory from '../viewer/delayer/viewer-delayer-factory';
import ViewerLifeCycleFactory from '../viewer/life-cycle/viewer-life-cycle-factory';
import ViewerManager from '../viewer/manager/viewer-manager';
import ViewerSourceIframeCreator from '../viewer/source/viewer-source-iframe-creator';
import ViewerSourceIframeFactory from '../viewer/source/viewer-source-iframe-factory';
import ViewerSourceVideoCreator from '../viewer/source/viewer-source-video-creator';
import ViewerSourceVideoFactory from '../viewer/source/viewer-source-video-factory';
import ViewerTypeWeb from '../viewer/type/viewer-type-web';
import ViewerTypeWidget from '../viewer/type/viewer-type-widget';
import VirtualKeyboardHiddenFactory from '../virtual-keyboard/virtual-keyboard-hidden-factory';
import VueFactory from '../vue/vue-factory';
import WebInternalBasePath from './basepath/web-basepath';
import WebControlListener from './control/web-control-listener';
import WebControlManager from './control/web-control-manager';
import WebControlParser from './control/web-control-parser';
import WebMemoryManager from './memory/web-memory-manager';
import WebSensorManager from './sensor/web-sensor-manager';
import WebSoundManager from './sound/web-sound-manager';
import WebStartupManager from './startup/web-startup-manager';
import WebTimeManager from './time/web-time-manager';
import ViewerTypeVideo from './video/viewer-type-video';
import WebNetworkListener from './web-network-listener';

import config from '../../config';
// @ts-expect-error Není to ESM.
import htmlImageBlank from '../html/html-image-blank.png';

// Inicializace IoC/DI knihovny.
// @ts-expect-error
Bottle.config.strict = true;
const bottle = new Bottle();
const common = new MainCommon(bottle).bootstrap();
// Deaktivovaný Prettier pro one-line definice závislostí a snazší merge.
// prettier-ignore
common.asyncEagerRegisterSession()
  // Inicializace a spuštění Player aplikace.
  .then(() => (
    common.registerCommon().bottle
      .constant('Config', config)
      .constant('Document', document)
      .constant('IcalApi', ICAL)
      .constant('LogicalUnitNameGenerator', () => uuidv4())
      .constant('Navigator', navigator)
      .constant('StorageManager', navigator.storage)
      .constant('UseStore', ContentSettingsStore.createUseStore())
      .constant('ViewerDelayerOptions', { delay: config.viewer.errorDelay, limit: config.viewer.errorLimit })
      .constant('Window', window)
      .constant('autorebootDefaultDisabled', config.autorebootDefaultDisabled)
      .constant('autorebootDefaultInterval', config.autorebootDefaultInterval)
      .constant('autorebootMinInterval', config.autorebootMinInterval)
      .constant('contentTaskMaxInterval', config.contentTaskMaxInterval)
      .constant('htmlIframeLoadDelay', config.htmlIframeLoadDelay)
      .constant('longTimersMaxDelay', config.longTimersMaxDelay)
      .constant('origin', location.origin)
      .constant('pdfConnectionTimeout', config.pdfConnectionTimeout)
      .constant('pdfSourceMax', config.pdfSourceMax)
      .constant('useExternalUnits', false)
      .constant('viewerControllerVideoPoster', htmlImageBlank)
      .service('AppManager', AppManager)
      .service('AssetDownloader', AssetDownloader, 'BasePathResolver', 'Downloader', 'FileArchiveManager', 'FileSystemManager', 'Logger', 'Store')
      .service('AutorebootManager', AutorebootManager, 'Logger', 'autorebootMinInterval', 'ReplacerPower')
      .service('AutorebootReplacer', AutorebootReplacer, 'autorebootDefaultDisabled', 'autorebootDefaultInterval', 'AutorebootManager')
      .service('BasePathResolver', BasePathResolver, 'InternalBasePathResolver', 'LogicalUnitMapper')
      .service('BrowserCapabilities', BrowserCapabilities, 'AppManager')
      .service('Communicator', Communicator)
      .service('ContentControllerFactory', ContentControllerFactory, 'BrowserCapabilities', 'Logger', 'UnixTimestamp', 'ViewerRegistry')
      .service('ContentDownloader', ContentDownloader, 'AssetDownloader', 'BasePathResolver', 'Config', 'FileSystemManager', 'FileSystemUnitFinder', 'Logger', 'LogicalUnitMapper', 'Store', 'Session', 'ViewerRegistry')
      .service('ContentSettingsStore', ContentSettingsStore, 'UseStore')
      .service('ContentTaskPlanCronFactory', ContentTaskPlanCronFactory, 'ContentTaskPlanIteratorFactory', 'LongTimers')
      .service('ContentTaskPlanIteratorFactory', ContentTaskPlanIteratorFactory, 'IcalApi', 'contentTaskMaxInterval')
      .service('ControlListener', WebControlListener, 'ControlParser')
      .service('ControlManager', WebControlManager, 'ControlListener')
      .service('ControlParser', WebControlParser)
      .service('DivViewerSourceCreator', DivViewerSourceCreator, 'PoolFactory', 'DivViewerSourceFactory')
      .service('DivViewerSourceFactory', DivViewerSourceFactory, 'PositionFrameFactory')
      .service('Downloader', Downloader, 'FileTransferManager')
      .service('FileArchiveManager', FileArchiveManager)
      .service('FileSystemManager', FileSystemManager, 'StorageManager')
      .service('FileSystemMapper', FileSystemMapper, 'FileSystemManager')
      .service('FileSystemUnitFinder', FileSystemUnitFinder, 'useExternalUnits')
      .service('FileTransferManager', FileTransferManager, 'FileSystemManager')
      .service('FirmwareManager', FirmwareManager, 'UaParser')
      .service('FocusManager', FocusNoop)
      .service('FocusNavigator', FocusNavigator, 'Document')
      .service('HtmlElementRepairer', HtmlElementRepairer)
      .service('HtmlMediaUtil', HtmlMediaUtil)
      .service('HtmlVideoRepairer', HtmlVideoRepairer, 'HtmlElementRepairer', 'HtmlMediaUtil')
      .service('ImageViewerControllerFactory', ImageViewerControllerFactory, 'ViewerLifeCycleFactory')
      .service('ImageViewerType', ImageFileViewerType, 'BasePathResolver', 'ImageViewerControllerFactory', 'FileSystemMapper', 'DivViewerSourceCreator')
      .service('InternalBasePathResolver', WebInternalBasePath, 'LogicalUnitMapper')
      .service('KioskViewerType', KioskViewerType, 'htmlIframeLoadDelay', 'ViewerSourceIframeCreator')
      .service('LoggerManager', LoggerManager, 'AppManager', 'FirmwareManager', 'Logger')
      .service('LogicalUnitMapper', SimpleLogicalUnitMapper, 'FileSystemManager', 'LogicalUnitNameGenerator')
      .service('LongTimers', LongTimers, 'longTimersMaxDelay')
      .service('MemoryManager', WebMemoryManager)
      .service('ViewerManager', ViewerManager, 'AnalyticsTracker', 'AppManager', 'ContentControllerFactory', 'ContentTaskPlanCronFactory', 'Logger')
      .service('NetworkListener', WebNetworkListener, 'Window', 'Navigator')
      .service('NetworkManager', NetworkManager)
      .service('PdfFileViewerType', PdfFileViewerType, 'BasePathResolver', 'PdfViewerControllerFactory', 'FileSystemMapper', 'PdfViewerSourceCreator')
      .service('PdfViewerControllerFactory', PdfViewerControllerFactory, 'origin', 'pdfConnectionTimeout', 'ViewerLifeCycleFactory')
      .service('PdfViewerSourceCreator', PdfViewerSourceCreator, 'pdfSourceMax', 'PoolAsyncFactory', 'ViewerSourceIframeFactory')
      .service('PoolAsyncFactory', PoolAsyncFactory)
      .service('PoolFactory', PoolFactory)
      .service('PositionFrameFactory', PositionFrameFactory)
      .service('PowerManager', PowerManager)
      .service('Replacer', Replacer, 'AutorebootReplacer', 'Logger', 'ReplacerControl', 'ReplacerPower', 'ReplacerReboot', 'ReplacerScreen', 'ReplacerSound')
      .service('ReplacerControl', ReplacerControl, 'ControlManager', 'Logger')
      .service('ReplacerPower', ReplacerPower, 'PowerManager')
      .service('ReplacerReboot', ReplacerReboot, 'Logger', 'ReplacerPower')
      .service('ReplacerScreen', ReplacerScreen, 'ScreenManager', 'Logger')
      .service('ReplacerSound', ReplacerSound, 'SoundManager')
      .service('ReportManager', ReportManager, 'AppManager', 'buildId', 'ControlManager', 'FileSystemManager', 'FirmwareManager', 'Logger', 'MemoryManager', 'NetworkManager', 'PowerManager', 'ScreenManager', 'SensorManager', 'Session', 'SoundManager', 'TimeManager', 'ViewerRegistry')
      .service('ScreenManager', ScreenManager)
      .service('SensorManager', WebSensorManager)
      .service('SoundManager', WebSoundManager)
      .service('StartupManager', WebStartupManager)
      .service('StreamViewerType', StreamViewerType, 'ViewerControllerVideoFactory', 'VideoViewerSourceCreator')
      .service('StatusInfoDialogOpenStrategySequence', StatusInfoDialogOpenStrategySequence, 'ControlListener')
      .service('StatusInfoDialogOpenStrategyShortcut', StatusInfoDialogOpenStrategyShortcut)
      .service('StatusInfoDialogController', StatusInfoDialogController, 'StatusInfoDialogOpenStrategySequence', 'StatusInfoDialogOpenStrategyShortcut')
      .service('TimeManager', WebTimeManager)
      .service('UaParser', UaParser)
      .service('UnixTimestamp', UnixTimestamp)
      .service('Updater', Updater, 'AppManager', 'buildId', 'Communicator', 'Logger', 'Session', 'Store')
      .service('VideoViewerSourceCreator', ViewerSourceVideoCreator, 'PoolFactory', 'HtmlVideoRepairer', 'VideoViewerSourceFactory')
      .service('VideoViewerSourceFactory', ViewerSourceVideoFactory, 'PositionFrameFactory')
      .service('VideoViewerType', ViewerTypeVideo, 'BasePathResolver', 'ViewerControllerVideoFactory', 'FileSystemMapper', 'VideoViewerSourceCreator')
      .service('ViewerControllerVideoFactory', ViewerControllerVideoFactory, 'BrowserCapabilities', 'viewerControllerVideoPoster')
      .service('ViewerDelayerFactory', ViewerDelayerFactory, 'ViewerDelayerOptions')
      .service('ViewerLifeCycleFactory', ViewerLifeCycleFactory)
      .service('ViewerRegistry', ViewerRegistry, 'ImageViewerType', 'KioskViewerType', 'PdfFileViewerType', 'StreamViewerType', 'VideoViewerType', 'ViewerTypeWeb', 'ViewerTypeWidget')
      .service('ViewerSourceIframeCreator', ViewerSourceIframeCreator, 'PoolFactory', 'ViewerSourceIframeFactory')
      .service('ViewerSourceIframeFactory', ViewerSourceIframeFactory, 'PositionFrameFactory')
      .service('ViewerTypeWeb', ViewerTypeWeb, 'htmlIframeLoadDelay', 'ViewerSourceIframeCreator')
      .service('ViewerTypeWidget', ViewerTypeWidget, 'htmlIframeLoadDelay', 'ViewerSourceIframeCreator')
      .service('VirtualKeyboardHiddenFactory', VirtualKeyboardHiddenFactory)
      .service('VirtualKeyboardManager', VirtualKeyboardManager, 'VirtualKeyboardHiddenFactory')
      .service('VueFactory', VueFactory)
  ))
  .then(() => common.start())
  .catch((error) => bottle.container.Logger.error(error));
